/**
 * Derives the Hazard Reference Table dataset by joining two atom sources:
 *
 * SOURCE 1 — `tabAtomMap['hazards']` → `DataModuleRow[]`
 *   Scenario-level rows. Each row represents one scenario variant of a hazard
 *   (e.g. "River Flooding: Avon 0.0m SLR, 1 in 10 year ARI").
 *   Multiple rows share the same `hazardName` (the parent layer).
 *   Already carries `suitability`, `confidenceRating`, `owner`, `group`.
 *
 * SOURCE 2 — `layerMetadataAtom` → `LayerMetadataRecord[]`
 *   Layer-level metadata authored by the CHAP team in the admin UI.
 *   Carries `limitations_caveats_md`, `source_links`, `description_md` etc.
 *   Identified by a base layer id (e.g. "h174").
 *
 * JOIN STRATEGY
 *   `DataModuleRow.type` holds the full layer identifier in the format
 *   "hazard::Risk source::h174". `getBaseLayerId` extracts the base id
 *   ("h174") which matches `LayerMetadataRecord.id`.
 *
 *   NOTE: `rep.id` is the scenario-level asset tag (e.g. "h174_l0_v3") —
 *   do NOT use it for the join. Always use `rep.type` as the join key.
 *
 * GROUPING
 *   Scenario rows are grouped by `hazardName` — one `HazardTableRow` per
 *   unique hazard layer. The first scenario in each group is used as the
 *   representative for identity fields (name, category, group, owner).
 *
 * SUITABILITY
 *   Preferred from `DataModuleRow.suitability` (already populated by the
 *   API response). Falls back to `LayerMetadataRecord.suitability` if the
 *   row-level suitability is empty.
 *
 * @returns rows — one `HazardTableRow` per hazard layer, sorted alphabetically
 * @returns useCases — unique use cases derived from suitability records,
 *   used to generate dynamic columns in `HazardTable`
 */

import { useAtomValue } from 'jotai'
import { HazardTableRow, UseCase } from '../types/hazard.types'
import { tabAtomMap } from '@app/modules/Home/Data/utils/data.submodule.utils'
import { layerMetadataAtom } from '@app/domains/layers/state/layers.atoms'
import React from 'react'
import { getBaseLayerId } from '@app/domains/layers/utils/layers.utils'

export const useHazardTableRows = (): { rows: HazardTableRow[]; useCases: UseCase[] } => {
  const dataRows = useAtomValue(tabAtomMap['hazards'])
  const layerMetadata = useAtomValue(layerMetadataAtom)

  return React.useMemo(() => {
    const grouped = new Map<string, typeof dataRows>()
    for (const row of dataRows) {
      const key = row.id
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key)!.push(row)
    }

    const rows: HazardTableRow[] = []

    for (const [hazardName, groupRows] of grouped.entries()) {
      const rep = groupRows[0]
      const baseId = getBaseLayerId(rep.type)
      const matched = layerMetadata.find((m) => m.id === baseId) ?? null
      const meta = matched?.metadata ?? null

      const suitability = rep.suitability?.length ? rep.suitability : (matched?.suitability ?? [])

      rows.push({
        id: rep.id,
        type: rep.type,
        unit: rep.unit ?? null,
        layerId: baseId,
        name: meta?.title ?? rep.name,
        hazardName,
        category: rep.category,
        group: rep.group,
        owner: rep.owner,
        ownerName: meta?.owner_name ?? null,
        ownerEmail: meta?.owner_email ?? null,
        ownerOrganization: meta?.owner_organization ?? null,
        confidenceRating: rep.confidenceRating ?? meta?.confidence_rating ?? null,
        lastUpdated: rep.lastUpdated,
        sourceLinks: meta?.source_links ?? [],
        limitationsCaveats: meta?.limitations_caveats_md ?? null,
        suitability,
        license: meta?.license ?? null,
        spatialExtent: meta?.spatial_extent ?? null,
        provider: meta?.provider ?? null,
        createdAt: meta?.created_at ?? null,
      })
    }

    rows.sort((a, b) => a.name.localeCompare(b.name))

    const seen = new Map<string, string>()
    for (const row of rows) {
      for (const s of row.suitability) {
        if (!seen.has(s.use_case_slug)) seen.set(s.use_case_slug, s.use_case_name)
      }
    }
    const useCases = Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }))

    return { rows, useCases }
  }, [dataRows, layerMetadata])
}
